import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ackReview, getReview, type ReviewDigest } from "../lib/api";
import { useTenant } from "../lib/tenant";
import { compactCount, tokenSummary } from "../lib/format";
import { Card, StatusDot, StatusPill, Btn, Icon, I } from "../kit";
import { SecLabel } from "./DashShell";

const POLL_MS = 60_000;
const SNIPPET = 120;

function snippet(text: string): string {
  const oneLine = text.replace(/\s+/g, " ").trim();
  return oneLine.length > SNIPPET ? `${oneLine.slice(0, SNIPPET)}…` : oneLine;
}

/** Today in the plane's ack-date shape (YYYY-MM-DD) — the fallback when the
 *  digest doesn't name its date. */
function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Morning review (phase 6 §3) — the team ritual, personal gets it too.
 *  Renders on Tonight above "Working now" (phase 7 Track A) ONLY when the
 *  last-night digest has activity the signed-in member hasn't acked yet:
 *  sessions as a checklist (each linking to its transcript), job outcomes,
 *  totals, who has reviewed, and a Mark-reviewed ack. Hidden on older
 *  planes (404). */
export function ReviewSection({
  userEmail,
  machineName,
}: {
  userEmail: string | undefined;
  machineName: (machineId: string) => string;
}) {
  const { tenantId } = useTenant();
  const [digest, setDigest] = useState<ReviewDigest | null>(null);
  // Local checklist state — purely a reading aid, not persisted.
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [acking, setAcking] = useState(false);
  const [justAcked, setJustAcked] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setDigest(await getReview());
    } catch {
      setDigest(null); // older plane (or transient error) — hide the section
    }
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps -- tenant rides every call; switching tenants must refetch

  useEffect(() => {
    setDigest(null);
    setChecked({});
    setJustAcked(false);
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  if (!digest) return null;
  const hasActivity = digest.sessions.length > 0 || digest.jobs.length > 0;
  const reviewedByMe =
    !!userEmail && digest.reviewedBy.some((r) => r.email === userEmail);
  // Already reviewed before this visit → nothing to surface. After acking
  // in-place we keep the section up so the confirmation is visible.
  if (!hasActivity || (reviewedByMe && !justAcked)) return null;

  const totals = digest.totals;
  const totalSessions = totals?.sessions ?? digest.sessions.length;
  const failedJobs =
    totals?.failedJobs ?? digest.jobs.filter((j) => j.state === "failed").length;
  const tokens = tokenSummary({
    inputTokens: totals?.inputTokens,
    outputTokens: totals?.outputTokens,
  });

  const ack = async () => {
    if (acking) return;
    setAcking(true);
    setErr(null);
    try {
      await ackReview(digest.date ?? todayDate());
      setJustAcked(true);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "ack failed");
    } finally {
      setAcking(false);
    }
  };

  const reviewers = digest.reviewedBy;

  return (
    <div>
      <SecLabel
        action={
          <span className="mono" style={{ fontSize: 10.5, color: "var(--color-fg-4)", whiteSpace: "nowrap" }}>
            {totalSessions} session{totalSessions === 1 ? "" : "s"}
            {tokens ? ` · ${tokens}` : ""}
            {failedJobs > 0
              ? ` · ${compactCount(failedJobs)} failed`
              : ""}
          </span>
        }
      >
        Overnight review
      </SecLabel>
      <Card pad={0} style={{ borderColor: "var(--color-accent-line)" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 16px",
            borderBottom: "1px solid var(--color-line-1)",
          }}
        >
          <Icon d={I.sun} size={16} style={{ color: "var(--color-warn)" }} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            What the swarm did while you slept
          </span>
          {digest.date && (
            <span
              className="mono"
              style={{
                marginLeft: "auto",
                fontSize: 11,
                color: "var(--color-fg-4)",
              }}
            >
              {digest.date}
            </span>
          )}
        </div>

        {digest.sessions.length > 0 && (
          <div className="dsh-review-rows">
            {digest.sessions.map((s) => {
              const line = s.lastAssistantLine || s.firstLine;
              const t = tokenSummary(s);
              const live = s.status === "running" || s.status === "starting";
              return (
                <div className="dsh-review-row" key={s.sessionId}>
                  <input
                    type="checkbox"
                    className="dsh-review-check"
                    aria-label={`mark ${s.agent ?? s.sessionId} as read`}
                    checked={checked[s.sessionId] ?? false}
                    onChange={(e) =>
                      setChecked((c) => ({
                        ...c,
                        [s.sessionId]: e.target.checked,
                      }))
                    }
                  />
                  <StatusDot
                    tone={live ? "live" : s.status === "error" ? "danger" : "off"}
                    pulse={live}
                    size={7}
                  />
                  <span className="agent">{s.agent ?? "session"}</span>
                  <span className="meta">
                    {s.machine ?? (s.machineId ? machineName(s.machineId) : "")}
                  </span>
                  {s.status && <span className="meta">{s.status}</span>}
                  {s.needsHuman && (
                    <span className="dsh-needs-you">needs you</span>
                  )}
                  <span className="spacer">
                    {t && <span className="meta">{t}</span>}
                    <Link className="dsh-link" to={`/sessions/${s.sessionId}`}>
                      transcript →
                    </Link>
                  </span>
                  {line && (
                    <span className="line" title={line}>
                      {snippet(line)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {digest.jobs.length > 0 && (
          <div className="dsh-review-rows">
            {digest.jobs.map((j, i) => (
              <div className="dsh-review-row" key={j.jobId ?? i}>
                <StatusPill status={j.state ?? "queued"}>
                  {j.state ?? "job"}
                </StatusPill>
                {j.agentName && <span className="agent">{j.agentName}</span>}
                <span className="meta">{j.scheduleName ?? j.source ?? ""}</span>
                {j.sessionId && (
                  <span className="spacer">
                    <Link className="dsh-link" to={`/sessions/${j.sessionId}`}>
                      session →
                    </Link>
                  </span>
                )}
                {j.error && (
                  <span className="line" style={{ color: "var(--color-danger)" }}>
                    {snippet(j.error)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="dsh-review-foot">
          <span className="hint">
            {reviewers.length === 0
              ? "no one has reviewed yet"
              : `reviewed by ${reviewers.map((r) => r.email).join(", ")}`}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            {err && <span className="dsh-hint-error">{err}</span>}
            {reviewedByMe || justAcked ? (
              <span className="mono" style={{ fontSize: 11, color: "var(--color-fg-4)" }}>
                you've reviewed
              </span>
            ) : (
              <Btn variant="primary" size="sm" disabled={acking} onClick={ack}>
                {acking ? "Marking…" : "Mark reviewed"}
              </Btn>
            )}
          </span>
        </div>
      </Card>
    </div>
  );
}
